import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, ArrowDownOnSquareStackIcon } from '@heroicons/react/24/outline';
import ordersService, { type AllDispatcher } from '../services/orders.service';
import { getRails, type RailOption } from '../services/rails.service';
import { EQUIPMENT_SIZES, EQUIPMENT_TYPES, CHASSIS_TYPES } from '../constants/orderFieldOptions';
import TypeAhead from './TypeAhead';
import PortLookupField, { type PortValue } from './PortLookupField';
import { DensityProvider } from '../contexts/DensityContext';
import { getBrokers, type LookupOption } from '../services/orderLookups.service';
import DateTimePicker from './DateTimePicker';
import { parseMdYDate, formatMdYDate, formatBackendDate } from '../utils/dateUtils';
import {
  EdiCreatePreview,
  EdiCreateAllResult,
  getEdiCreatePreview,
  createAllEdiOrders,
} from '../services/pendingEdiOrders.service';

// =============================================================================
// EdiCreateAllModal — the faithful multi-row Create All Orders form (V1
// edi_createallorders). One row per eligible pending order, each editable; a
// per-column "copy to all" (first row's value → every row), and "use one master
// pro for all". Carrier / contacts / pickup+terminate locations default from each
// row's create-preview (edit those in the single Create dialog). For IMPORT the ETA
// is required — rows left blank are flagged and skipped server-side.
// =============================================================================

interface RowState {
  recno: number;
  preview: EdiCreatePreview;
  fields: Record<string, any>;
  /** The pickup this row will create with. Seeded from the server's resolution, which
   *  leaves it 0 when the template and the EDI disagree — the user picks. */
  pickupcode: number;
  /** Same, for terminate. */
  terminatecode: number;
}

// Match location codes, not display names or template/EDI origins.
const canCopyPickup = (rows: RowState[]): boolean => {
  const code = rows[0]?.pickupcode;
  return rows.length > 1 && code > 0 && rows.every(({ preview }) =>
    !preview.pickup?.locked && preview.pickup?.source === 'choice'
    && preview.pickup.candidates.some((candidate) => candidate.code === code));
};

interface Props {
  recnos: number[];
  title: string;
  onClose: () => void;
  onDone: (result: EdiCreateAllResult) => void;
}

type ColType = 'text' | 'textarea' | 'date' | 'flag' | 'dispatcher' | 'broker' | 'pickup' | 'terminate' | 'port' | 'size' | 'equiptype' | 'chassisprovider' | 'chassistype' | 'terminal';

// A port cell is two fields, not one: the recno the create posts, and the port name it
// displays. They have to travel together on an edit AND on a copy-to-all — copying the
// recno alone would leave a row showing its own port's name against row 1's recno.
const PORT_NAME_FIELD: Record<string, string> = {
  portoforigin: 'portoforiginname',
  portofdischarge: 'portofdischargename',
};

const COLUMNS: {
  key: string; label: string; type: ColType;
  /** Column width in px. Authoritative: it drives the <colgroup> under a FIXED
   *  table layout, so the rendered width is exactly this and long values clip
   *  instead of stretching the column. */
  width: number; upper?: boolean;
  /** Suppress the copy-to-all arrow — for columns whose valid values differ per row. */
  noCopyAll?: boolean;
}[] = [
  { key: 'masterpro', label: 'Master Pro', type: 'text', width: 112 },
  // Pickup can only copy when every row offers the selected location.
  { key: 'pickupcode', label: 'Pickup', type: 'pickup', width: 224 },
  // Same per-row resolution as Pickup, but never locked — there is no terminate
  // override table, so it is only ever "the template and the EDI disagree" or settled.
  { key: 'terminatecode', label: 'Terminate', type: 'terminate', width: 224, noCopyAll: true },
  { key: 'dispatcher', label: 'Dispatcher', type: 'dispatcher', width: 160 },
  { key: 'brokercode', label: 'Broker', type: 'broker', width: 208 },
  { key: 'size', label: 'Size', type: 'size', width: 80 },
  { key: 'equiptype', label: 'Type', type: 'equiptype', width: 128 },
  { key: 'chassisprovider', label: 'Chassis Prov', type: 'chassisprovider', width: 144 },
  // 'Chassis Type' values run long ("48 SINGLE INLINE"), and it was the worst
  // offender in the squished layout.
  { key: 'chassistype', label: 'Chassis Type', type: 'chassistype', width: 176 },
  // The flag selects are one character of value, but a native select still needs
  // room for its chevron — w-12 clipped it against the value.
  { key: 'isdrop', label: 'Drop', type: 'flag', width: 64 },
  { key: 'hot', label: 'Hot', type: 'flag', width: 64 },
  { key: 'hazmat', label: 'Haz', type: 'flag', width: 64 },
  // Date cells carry a picker button and a clear (×) inside the field.
  { key: 'etadate', label: 'ETA', type: 'date', width: 144 },
  { key: 'lastfreedaydate', label: 'LFD', type: 'date', width: 144 },
  // Ports sit ahead of Terminal / Vessel / Voyage, the same ocean-info grouping (and
  // the same order) the single Create dialog uses.
  { key: 'portoforigin', label: 'Port of Origin', type: 'port', width: 192 },
  { key: 'portofdischarge', label: 'Port of Discharge', type: 'port', width: 192 },
  { key: 'rail', label: 'Terminal', type: 'terminal', width: 144 },
  { key: 'vessel', label: 'Vessel', type: 'text', width: 160, upper: true },
  { key: 'voyage', label: 'Voyage', type: 'text', width: 96 },
  { key: 'commodity', label: 'Commodity', type: 'text', width: 128 },
  // A textarea, not a text input: comments arrive from EDI with newlines in them,
  // and an <input> runs the value sanitization algorithm on assignment — it STRIPS
  // CR/LF. That silently joined 'LINE ONE\nLINE TWO' into 'LINE ONELINE TWO', and
  // worse, the stripped value is what came back out of onChange, so typing a single
  // character in the cell destroyed the line breaks on save.
  { key: 'comments', label: 'Comments', type: 'textarea', width: 224 },
];

const CELL = 'w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-800 dark:text-white disabled:bg-slate-100 disabled:text-slate-400 dark:disabled:bg-gray-900';

// create-preview returns ISO dates; the app's date fields are MM/DD/YYYY.
const isoToMdy = (v: any): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? ''));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : String(v ?? '');
};

// Orange cue (matches TypeAhead) when a template-copied value is no longer an active option.
const STALE_CLS = 'bg-orange-50 dark:bg-orange-900/20 border-orange-400 dark:border-orange-500';
const STALE_TITLE = 'This value is no longer an active choice — verify or update it.';
const staleCls = (val: any, valid: Set<string>): string => {
  const v = String(val ?? '').trim();
  return v !== '' && valid.size > 0 && !valid.has(v) ? STALE_CLS : '';
};
const TH = 'px-2 py-1.5 text-left text-[0.625rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 whitespace-nowrap';

// The field columns at a legible width are wider than any screen, so the grid scrolls
// sideways. The three identity columns pin to the left edge, so you can still tell
// which order a row is while editing a column off to the right.
//
// Their `left` offsets are the running total of the widths before them, computed
// rather than written down — a hand-written offset silently misaligns the moment a
// width changes. This is also why the table runs a FIXED layout with a <colgroup>:
// under the default auto layout a long container number would stretch the Order
// column past its declared width and every offset after it would be wrong.
const ID_COLS = [
  { label: '#', width: 32 },
  { label: 'Order', width: 112 },
  { label: 'Template', width: 80 },
].map((c, i, all) => ({ ...c, left: all.slice(0, i).reduce((n, p) => n + p.width, 0) }));

const GRID_WIDTH = [...ID_COLS, ...COLUMNS].reduce((n, c) => n + c.width, 0);

// Opaque background is required: a sticky cell scrolls OVER the cells beside it, and
// a transparent one would let them show through. The row tint is applied per-cell for
// the same reason (a <tr> background does not paint under a sticky child), which is
// what the `group`/`group-hover` pairing on the row is for.
//
// The tint MUST be fully opaque, and that is the whole reason it is a flat colour
// rather than the usual translucent wash. A `bg-blue-50/40` hover does not layer over
// the `bg-white` beneath it — it REPLACES it, so the sticky cells turned translucent
// on hover and the scrolled-under columns showed straight through them. Same colour
// on the sticky and ordinary cells, so the highlight reads as one continuous row.
const ROW_TINT = 'group-hover:bg-blue-50 dark:group-hover:bg-slate-800';
const ROW_TD = `px-1.5 py-1 ${ROW_TINT}`;
const ID_TD = `sticky z-10 overflow-hidden bg-white px-2 py-1 dark:bg-gray-900 ${ROW_TINT}`;

const EdiCreateAllModal: React.FC<Props> = ({ recnos, title, onClose, onDone }) => {
  const [rows, setRows] = useState<RowState[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [useOneMasterpro, setUseOneMasterpro] = useState(false);

  const [dispatchers, setDispatchers] = useState<AllDispatcher[]>([]);
  const [rails, setRails] = useState<RailOption[]>([]);
  const [chassisProviders, setChassisProviders] = useState<string[]>([]);
  const [brokers, setBrokers] = useState<LookupOption[]>([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    Promise.all(
      recnos.map((r) => getEdiCreatePreview(r)
        .then((p) => ({
          recno: r, preview: p,
          fields: { ...p.fields, etadate: isoToMdy(p.fields.etadate), lastfreedaydate: isoToMdy(p.fields.lastfreedaydate) },
          pickupcode: Number(p.pickup?.code) || 0,
          terminatecode: Number(p.terminate?.code) || 0,
        } as RowState))
        .catch(() => null)),
    )
      .then((res) => { if (alive) { setRows(res.filter(Boolean) as RowState[]); setLoading(false); } })
      .catch((e) => { if (alive) { setError(e?.message || 'Failed to load the orders.'); setLoading(false); } });
    return () => { alive = false; };
  }, [recnos]);

  useEffect(() => {
    ordersService.getAllDispatchers().then((r) => setDispatchers(r.data)).catch(() => {});
    getRails().then(setRails).catch(() => {});
    ordersService.getChassisProviders().then(setChassisProviders).catch(() => {});
    getBrokers().then(setBrokers).catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const dispatcherOptions = useMemo(
    () => dispatchers.map((d) => ({ value: d.username, label: d.gwi ? d.displayname : `${d.displayname} (non-GWI)` })),
    [dispatchers],
  );
  const dispatcherSet = useMemo(() => new Set(dispatchers.map((d) => d.username)), [dispatchers]);
  const railSet = useMemo(() => new Set(rails.map((r) => r.terminal)), [rails]);
  const sizeSet = useMemo(() => new Set(EQUIPMENT_SIZES as string[]), []);
  const typeSet = useMemo(() => new Set(EQUIPMENT_TYPES as string[]), []);
  const chassisProviderSet = useMemo(() => new Set(chassisProviders), [chassisProviders]);
  const chassisTypeSet = useMemo(() => new Set(CHASSIS_TYPES as string[]), []);

  // Pickup / terminate live on the row rather than in `fields` — they are resolved
  // server-side and posted as their own arguments, not as order columns.
  const setLocationCell = (idx: number, key: 'pickupcode' | 'terminatecode', code: number) =>
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, [key]: code } : r)));

  // A port picker hands back {recno, name}; store both so the cell keeps displaying
  // the name while the create still posts the recno.
  const setPortCell = (idx: number, key: string, v: PortValue) =>
    setRows((rs) => rs.map((r, i) => (i === idx
      ? { ...r, fields: { ...r.fields, [key]: v.recno, [PORT_NAME_FIELD[key]]: v.name } }
      : r)));

  const setCell = (idx: number, key: string, value: any) =>
    setRows((rs) => rs.map((r, i) => (i === idx
      ? { ...r, fields: { ...r.fields, [key]: value,
          // A broker contact belongs to one broker. This grid has no contact column —
          // the value is whatever the row's template carried — so changing the broker
          // has to drop it, or the create writes another broker's contact.
          ...(key === 'brokercode' ? { brokercontactrecno: '' } : {}) } }
      : r)));

  // Copy the FIRST row's value for a column into every row (V1 copytoall).
  const copyToAll = (key: string) =>
    setRows((rs) => {
      if (key === 'pickupcode') {
        return canCopyPickup(rs) ? rs.map((r) => ({ ...r, pickupcode: rs[0].pickupcode })) : rs;
      }
      return (rs.length ? rs.map((r, i) => (i === 0 ? r : { ...r, fields: { ...r.fields, [key]: rs[0].fields[key],
      // Ports copy as a pair — see PORT_NAME_FIELD.
      ...(PORT_NAME_FIELD[key] ? { [PORT_NAME_FIELD[key]]: rs[0].fields[PORT_NAME_FIELD[key]] } : {}),
      ...(key === 'brokercode' ? { brokercontactrecno: '' } : {}) } })) : rs);
    });

  // Rows whose template and EDI name different pickups and which the user hasn't
  // resolved yet. The server refuses these rather than picking a side, so Create is
  // blocked until they're settled.
  const pickupUnresolvedCount = useMemo(
    () => rows.filter((r) => (r.preview.pickup?.source === 'choice' && !r.pickupcode)
      || (r.preview.terminate?.source === 'choice' && !r.terminatecode)).length,
    [rows],
  );

  const etaMissingCount = useMemo(
    () => rows.filter((r) => r.preview.is_import && !String(r.fields.etadate || '').trim()).length,
    [rows],
  );

  const submit = () => {
    if (saving || !rows.length || pickupUnresolvedCount > 0) return;
    setSaving(true);
    setError(null);
    const recnoOrBlank = (v: any) => (Number(v) > 0 ? String(Number(v)) : '');
    const items = rows.map((r) => {
      const fields: Record<string, any> = {
        ...r.fields,
        etadate: formatBackendDate(r.fields.etadate) || '',
        lastfreedaydate: formatBackendDate(r.fields.lastfreedaydate) || '',
        carriercode: recnoOrBlank(r.fields.carriercode),
        portoforigin: recnoOrBlank(r.fields.portoforigin),
        portofdischarge: recnoOrBlank(r.fields.portofdischarge),
        billtocontactrecno: recnoOrBlank(r.fields.billtocontactrecno),
        brokercode: recnoOrBlank(r.fields.brokercode),
        brokercontactrecno: recnoOrBlank(r.fields.brokercontactrecno),
      };
      delete fields.portoforiginname;
      delete fields.portofdischargename;
      return {
        recno: r.recno,
        fields,
        pickupcode: r.pickupcode || null,
        terminatecode: r.terminatecode || null,
      };
    });
    createAllEdiOrders(items, useOneMasterpro)
      .then((res) => onDone(res))
      .catch((e) => { setSaving(false); setError(e?.response?.data?.detail || e?.message || 'The batch create failed.'); });
  };

  const renderCell = (row: RowState, idx: number, col: typeof COLUMNS[number]) => {
    const raw = row.fields[col.key];
    const val = String(raw ?? (col.type === 'flag' ? 'N' : ''));
    const disabled = col.key === 'masterpro' && useOneMasterpro;
    switch (col.type) {
      case 'flag':
        return (
          <select className={CELL} value={val} onChange={(e) => setCell(idx, col.key, e.target.value)}>
            <option value="N">N</option><option value="Y">Y</option>
          </select>
        );
      case 'date':
        return (
          <DateTimePicker
            mode="date" inputVariant="compact" selectionType="single" autoApply usePortal
            openDirection="down" autoFlip labelType="none" className="w-full [&_div]:!min-w-0"
            value={parseMdYDate(val)}
            onChange={(v) => setCell(idx, col.key, formatMdYDate(v as Date | null))}
          />
        );
      case 'dispatcher':
        return (
          <select className={`${CELL} ${staleCls(val, dispatcherSet)}`} title={staleCls(val, dispatcherSet) ? STALE_TITLE : undefined}
            value={val} onChange={(e) => setCell(idx, col.key, e.target.value)}>
            <option value="">—</option>
            {dispatcherOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            {val && !dispatcherOptions.some((o) => o.value === val) && <option value={val}>{val}</option>}
          </select>
        );
      case 'pickup':
      case 'terminate': {
        const isPickup = col.type === 'pickup';
        const res = isPickup ? row.preview.pickup : row.preview.terminate;
        const stateKey = isPickup ? 'pickupcode' : 'terminatecode';
        const chosen = isPickup ? row.pickupcode : row.terminatecode;
        if (res?.locked) {
          return (
            <span className="block truncate text-slate-500 dark:text-gray-400"
              title={`Locked by EDI Pickup Location Mapping #${res.mapping_recno}`}>
              🔒 {res.name || `#${res.code}`}
            </span>
          );
        }
        if (res?.source === 'choice') {
          return (
            <select
              className={`${CELL} ${chosen ? '' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-400'}`}
              title={`The template and the EDI name different ${isPickup ? 'pickups' : 'terminates'} — choose one`}
              value={chosen ? String(chosen) : ''}
              onChange={(e) => setLocationCell(idx, stateKey, Number(e.target.value) || 0)}>
              <option value="">— choose —</option>
              {res.candidates.map((c) => (
                <option key={`${c.origin}-${c.code}`} value={c.code}>
                  {c.origin === 'template' ? 'Template: ' : 'EDI: '}{c.name || `#${c.code}`}
                </option>
              ))}
            </select>
          );
        }
        // One side only (or neither) — nothing to decide. Read-only here, editable in
        // the single Create dialog, same as ports / carrier / contacts.
        return (
          <span className="block truncate text-slate-500 dark:text-gray-400">
            {res?.name || (res?.code ? `#${res.code}` : '—')}
          </span>
        );
      }
      case 'port':
        // Same picker as the single Create dialog, stripped to the bare input for a
        // table cell. Typing without picking unlinks (recno 0), which posts blank and
        // lets the template's port stand — matching every other recno cell here.
        return (
          <PortLookupField
            label={col.label}
            hideLabel
            hideStatus
            value={{ recno: Number(raw) || 0, name: String(row.fields[PORT_NAME_FIELD[col.key]] ?? '') }}
            onChange={(v) => setPortCell(idx, col.key, v)}
          />
        );
      case 'broker':
        // ~3k active brokers — a searchable TypeAhead, same widget the Pro Detail
        // Addl Info tab uses, rather than a select nobody could scroll.
        return (
          <TypeAhead
            options={brokers}
            value={val && val !== '0' ? val : ''}
            onChange={(next) => setCell(idx, col.key, next || '')}
            placeholder="Broker…"
            showSearch
            showDescriptions
            compact
            ariaLabel="Broker/Forwarder"
          />
        );
      case 'size':
        return <select className={`${CELL} ${staleCls(val, sizeSet)}`} title={staleCls(val, sizeSet) ? STALE_TITLE : undefined}
          value={val} onChange={(e) => setCell(idx, col.key, e.target.value)}>
          {EQUIPMENT_SIZES.map((s) => <option key={s} value={s}>{s || '—'}</option>)}</select>;
      case 'equiptype':
        return <select className={`${CELL} ${staleCls(val, typeSet)}`} title={staleCls(val, typeSet) ? STALE_TITLE : undefined}
          value={val} onChange={(e) => setCell(idx, col.key, e.target.value)}>
          {EQUIPMENT_TYPES.map((t) => <option key={t} value={t}>{t || '—'}</option>)}</select>;
      case 'chassisprovider':
        // Free-form list from cms (no static constant), so an empty option is needed —
        // blank means "let the template's provider stand" on the server.
        return <select className={`${CELL} ${staleCls(val, chassisProviderSet)}`} title={staleCls(val, chassisProviderSet) ? STALE_TITLE : undefined}
          value={val} onChange={(e) => setCell(idx, col.key, e.target.value)}>
          <option value="">—</option>
          {chassisProviders.map((c) => <option key={c} value={c}>{c}</option>)}</select>;
      case 'chassistype':
        return <select className={`${CELL} ${staleCls(val, chassisTypeSet)}`} title={staleCls(val, chassisTypeSet) ? STALE_TITLE : undefined}
          value={val} onChange={(e) => setCell(idx, col.key, e.target.value)}>
          {CHASSIS_TYPES.map((t) => <option key={t} value={t}>{t || '—'}</option>)}</select>;
      case 'terminal':
        return <select className={`${CELL} ${staleCls(val, railSet)}`} title={staleCls(val, railSet) ? STALE_TITLE : undefined}
          value={val} onChange={(e) => setCell(idx, col.key, e.target.value)}>
          <option value="">—</option>{rails.map((r) => <option key={r.terminal} value={r.terminal}>{r.terminal}</option>)}</select>;
      case 'textarea': {
        // Show up to three lines so a multi-line comment reads as multi-line, while a
        // one-line comment keeps the row as compact as every other cell. Anything
        // longer is a drag away (resize-y) or in the tooltip.
        const lines = Math.min(3, String(val ?? '').split('\n').length);
        return <textarea className={`${CELL} block resize-y leading-tight`} rows={lines}
          value={val} disabled={disabled} title={String(val ?? '') || undefined}
          onChange={(e) => setCell(idx, col.key, e.target.value)} />;
      }
      default:
        return <input className={CELL} value={val} disabled={disabled}
          onChange={(e) => setCell(idx, col.key, col.upper ? e.target.value.toUpperCase() : e.target.value)}
          // Under "use one master pro" every cell is disabled and empty, so the
          // placeholder is the only thing saying which pro each row ends up on —
          // and "(new pro #)" on all of them read as "each row masters itself",
          // which is exactly the bug it sat next to.
          placeholder={col.key !== 'masterpro' ? undefined
            : useOneMasterpro ? (idx === 0 ? '(new pro # — the shared master)' : '(the 1st order\u2019s pro #)')
            : '(new pro #)'} />;
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[130] bg-slate-900/40 flex items-center justify-center p-4" role="dialog" aria-label="Create All Orders"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-slate-200 dark:border-gray-700 w-full max-w-[98vw] max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-heading">Create All Orders</h2>
            <p className="text-xs text-slate-500 dark:text-gray-400 truncate">{title} · {rows.length} order{rows.length === 1 ? '' : 's'}</p>
          </div>
          <button type="button" onClick={() => !saving && onClose()} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2 border-b border-slate-200 dark:border-gray-700 text-xs">
          <label className="inline-flex items-center gap-1.5 text-slate-700 dark:text-gray-200">
            <input type="checkbox" checked={useOneMasterpro} onChange={(e) => setUseOneMasterpro(e.target.checked)} className="accent-emerald-600" />
            Use one Master Pro for all
          </label>
          {rows.length > 1 && (
            // The copy-to-all buttons went unused because a bare arrow in a column
            // header reads as sorting. Naming the action once, with the same icon
            // that sits in the headers, is what makes it findable.
            <span className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-slate-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300">
              <ArrowDownOnSquareStackIcon className="w-3.5 h-3.5 shrink-0 text-slate-500 dark:text-slate-300" />
              <span>
                Copies the <span className="font-medium">first row&rsquo;s</span> value down to every row.
              </span>
            </span>
          )}
          <span className="text-slate-400">Carrier / contacts default from each order — edit those in single Create.</span>
          {pickupUnresolvedCount > 0 && (
            <span className="text-amber-600 dark:text-amber-400">
              {pickupUnresolvedCount} order{pickupUnresolvedCount === 1 ? '' : 's'} need a location chosen — the template and EDI differ.
            </span>
          )}
          {etaMissingCount > 0 && (
            <span className="text-amber-600 dark:text-amber-400">{etaMissingCount} IMPORT order{etaMissingCount === 1 ? '' : 's'} missing an ETA — today's date will be used.</span>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-auto">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted">Loading orders…</div>
          ) : error ? (
            <div className="m-3 rounded-md border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">{error}</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted">No eligible orders to create.</div>
          ) : (
            // The grid is a compact region, so every density-aware descendant (the
            // broker and port TypeAheads) renders at cell scale without each call site
            // threading `compact` — see DensityContext.
            <DensityProvider compact>
            <table className="table-fixed text-xs border-collapse" style={{ width: GRID_WIDTH }}>
              <colgroup>
                {[...ID_COLS, ...COLUMNS].map((c, i) => (
                  <col key={i} style={{ width: c.width }} />
                ))}
              </colgroup>
              {/* Sticky lives on the CELLS, not on <thead>/<tr>: under
                  border-collapse those two are unreliable, th/td are not. */}
              <thead>
                <tr>
                  {ID_COLS.map((c, i) => (
                    <th
                      key={c.label}
                      style={{ left: c.left }}
                      className={`${TH} sticky top-0 z-30 bg-slate-100 dark:bg-slate-800${i === ID_COLS.length - 1 ? ' border-r border-slate-300 dark:border-gray-600' : ''}`}
                    >
                      {c.label}
                    </th>
                  ))}
                  {COLUMNS.map((c) => {
                    const canCopy = rows.length > 1 && !c.noCopyAll
                      && !(c.key === 'masterpro' && useOneMasterpro);
                    return (
                      <th key={c.key} className={`${TH} sticky top-0 z-20 bg-slate-100 dark:bg-slate-800`}>
                        <div className="flex items-center justify-between gap-1">
                          <span>{c.label}</span>
                          {canCopy && (
                            // Chip, not a bare glyph: this used to be a plain down-arrow
                            // that read as a sort control (users did not find the copy at
                            // all). A bordered button with a copy-down icon says "action",
                            // and the header strip above spells out what it does.
                            <button
                              type="button"
                              onClick={() => copyToAll(c.key)}
                              disabled={c.key === 'pickupcode' && !canCopyPickup(rows)}
                              title={c.key === 'pickupcode' && !canCopyPickup(rows)
                                ? 'Select a pickup in the first row that is available in every pickup dropdown. Locked or read-only pickups cannot be copied.'
                                : `Copy the first row's ${c.label} down to all ${rows.length} rows`}
                              aria-label={`Copy the first row's ${c.label} down to all rows`}
                              className="disabled:opacity-40 disabled:cursor-not-allowed shrink-0 rounded border border-slate-300 bg-white p-0.5 text-slate-500 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/30 dark:hover:text-blue-300"
                            >
                              <ArrowDownOnSquareStackIcon className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {rows.map((row, idx) => (
                  <tr key={row.recno} className="group">
                    <td style={{ left: ID_COLS[0].left }} className={`${ID_TD} text-slate-400 tabular-nums`}>
                      {idx + 1}
                    </td>
                    <td style={{ left: ID_COLS[1].left }} className={`${ID_TD} text-slate-600 dark:text-gray-300`}>
                      #{row.recno}
                      {/* Container numbers are 11 chars and the column is fixed — clip
                          rather than let it push the sticky offsets out of line. */}
                      <div className="truncate text-[0.625rem] text-slate-400">
                        {row.preview.from_edi.container || row.preview.movetype}
                      </div>
                    </td>
                    <td style={{ left: ID_COLS[2].left }} className={`${ID_TD} border-r border-slate-300 text-slate-600 dark:border-gray-600 dark:text-gray-300`}>
                      {row.preview.template}
                    </td>
                    {COLUMNS.map((c) => (
                      <td key={c.key} className={ROW_TD}>
                        {renderCell(row, idx, c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            </DensityProvider>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-2.5 border-t border-slate-200 dark:border-gray-700">
          <button type="button" onClick={() => !saving && onClose()}
            className="px-4 py-1.5 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={saving || loading || !rows.length || pickupUnresolvedCount > 0}
            className="px-5 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            {saving ? 'Creating…' : `Create ${rows.length} Order${rows.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EdiCreateAllModal;
